import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';

/**
 * CUALia — Capa de Conciencia.
 *
 * Componente overlay/toast contextual que muestra consejo educativo
 * en momentos clave (registro, transacciones, KYC, etc.). NO bloquea,
 * solo informa. El usuario decide.
 *
 * Uso:
 *   <CualiaAdvice
 *     contextType="large_transaction"
 *     metadata={{ amount_cc: 500 }}
 *     onAcknowledge={() => proceedWithAction()}
 *     onCancel={() => abortAction()}
 *   />
 *
 * Tambien expone:
 *   CualiaPreferences — pequeno selector de tono (femenino/masculino/neutro)
 *   triggerCualia(contextType, metadata) — helper para invocar el overlay programaticamente
 */
const CualiaAdvice = ({ contextType, metadata, onAcknowledge, onCancel, allowDismiss = true, autoFetch = true }) => {
  const [advice, setAdvice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!autoFetch) return;
    let cancelled = false;
    const fetchAdvice = async () => {
      setLoading(true); setError(null);
      try {
        const r = await axios.post(`${API}/cualia/advice`, {
          context_type: contextType || 'default',
          metadata: metadata || {},
        });
        if (!cancelled) setAdvice(r.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'No se pudo obtener consejo');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAdvice();
    return () => { cancelled = true; };
  }, [contextType, JSON.stringify(metadata || {}), autoFetch]);

  if (!advice && !loading && !error) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="cualia-advice-overlay">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-5 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white font-bold">C</div>
          <div>
            <p className="text-white font-semibold text-sm" data-testid="cualia-name">CUALia</p>
            <p className="text-white/80 text-xs">Tu asistente de conciencia</p>
          </div>
        </div>
        <div className="p-5 space-y-3">
          {loading && <p className="text-sm text-gray-500" data-testid="cualia-loading">CUALia esta pensando...</p>}
          {error && <p className="text-sm text-red-600" data-testid="cualia-error">{error}</p>}
          {advice && (
            <>
              <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line" data-testid="cualia-advice-text">{advice.advice}</p>
              <p className="text-xs text-gray-400 italic">{advice.footer}</p>
            </>
          )}
        </div>
        <div className="px-5 py-3 bg-gray-50 flex justify-end gap-2 border-t">
          {allowDismiss && (
            <button onClick={onCancel} data-testid="cualia-cancel"
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
              Cancelar
            </button>
          )}
          <button onClick={onAcknowledge} data-testid="cualia-ack"
            disabled={loading}
            className="px-4 py-1.5 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed">
            Entendido, continuar
          </button>
        </div>
      </div>
    </div>
  );
};

export const CualiaPreferences = () => {
  const [tone, setTone] = useState('femenino');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    axios.get(`${API}/cualia/preferences`)
      .then(r => setTone(r.data.voice_tone || 'femenino'))
      .catch(() => {});
  }, []);

  const save = async (newTone) => {
    setSaving(true); setMsg(null);
    try {
      await axios.post(`${API}/cualia/preferences`, { voice_tone: newTone });
      setTone(newTone);
      setMsg('Preferencia guardada');
      setTimeout(() => setMsg(null), 2000);
    } catch (e) {
      setMsg('Error guardando');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="cualia-preferences">
      <h4 className="text-sm font-semibold text-gray-800 mb-2">Tono de voz de CUALia</h4>
      <p className="text-xs text-gray-500 mb-3">Elige como prefieres que CUALia te hable. Esta preferencia es opaca y NO clasifica tu genero.</p>
      <div className="space-y-1">
        {['femenino', 'masculino', 'neutro'].map(t => (
          <label key={t} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="radio" name="cualia-tone" value={t} checked={tone === t}
              data-testid={`cualia-tone-${t}`}
              disabled={saving} onChange={() => save(t)} />
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </label>
        ))}
      </div>
      {msg && <p className="text-xs text-emerald-700 mt-2">{msg}</p>}
    </div>
  );
};

export default CualiaAdvice;
